import { useState, useEffect, useCallback, useRef } from "react";
import { Bot, Check, X, Sparkles, Loader2, RefreshCw, ChevronDown, Clock } from "lucide-react";
import { toast } from "sonner";

function DraftCard({ draft, onApprove, onReject, busy }) {
  const [showEssays, setShowEssays] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl p-4" data-testid={`draft-${draft.topic_id}`}>
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <span className="text-[10px] font-semibold tracking-wider uppercase text-neutral bg-gray-100 px-2 py-0.5 rounded-full">{draft.category}</span>
        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-view-a bg-view-a-light px-2 py-0.5 rounded-full"><Bot className="w-3 h-3" /> BOT DRAFT</span>
        {draft.seed_topic && <span className="text-[10px] text-neutral/70 italic">from: {draft.seed_topic}</span>}
      </div>
      <h3 className="text-sm font-semibold text-dark leading-snug mb-2">{draft.question}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
        <div className="p-2.5 bg-view-a-light/50 rounded-lg">
          <p className="text-[11px] font-bold text-view-a uppercase tracking-wider">{draft.side_a_label}</p>
          <p className="text-xs text-dark/75 mt-0.5 leading-snug">{draft.side_a_text}</p>
        </div>
        <div className="p-2.5 bg-view-b-light/50 rounded-lg">
          <p className="text-[11px] font-bold text-view-b uppercase tracking-wider">{draft.side_b_label}</p>
          <p className="text-xs text-dark/75 mt-0.5 leading-snug">{draft.side_b_text}</p>
        </div>
      </div>

      <button onClick={() => setShowEssays((s) => !s)} className="flex items-center gap-1 text-[11px] font-semibold text-neutral hover:text-dark transition-colors mb-2" data-testid={`draft-essays-toggle-${draft.topic_id}`}>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showEssays ? "rotate-180" : ""}`} /> Full arguments
        <span className="text-neutral/60">({(draft.side_a_essay || "").split(/\s+/).filter(Boolean).length} / {(draft.side_b_essay || "").split(/\s+/).filter(Boolean).length} words)</span>
      </button>
      {showEssays && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3 max-h-64 overflow-y-auto">
          <div className="text-[12px] leading-5 text-dark/80 whitespace-pre-line">{draft.side_a_essay}</div>
          <div className="text-[12px] leading-5 text-dark/80 whitespace-pre-line">{draft.side_b_essay}</div>
        </div>
      )}

      <div className="flex gap-2">
        <button onClick={() => onApprove(draft.topic_id)} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-success rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50" data-testid={`approve-draft-${draft.topic_id}`}>
          <Check className="w-3.5 h-3.5" /> Approve &amp; Publish
        </button>
        <button onClick={() => onReject(draft.topic_id)} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-view-b bg-view-b-light rounded-lg hover:bg-view-b/20 transition-colors disabled:opacity-50" data-testid={`reject-draft-${draft.topic_id}`}>
          <X className="w-3.5 h-3.5" /> Reject
        </button>
      </div>
    </div>
  );
}

export default function BotDraftsPanel({ api, onPublished }) {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const fetchDrafts = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/topics/drafts");
      setDrafts(data);
      return data;
    } catch {
      return [];
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchDrafts();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchDrafts]);

  const generateNow = async () => {
    setGenerating(true);
    try {
      await api.post("/admin/bot/generate-now");
      toast.success("Generating a debate draft… (~1 min)", { icon: <Sparkles className="w-4 h-4" /> });
      const startCount = drafts.length;
      let ticks = 0;
      pollRef.current = setInterval(async () => {
        ticks += 1;
        const data = await fetchDrafts();
        if (data.length > startCount || ticks >= 14) {
          clearInterval(pollRef.current);
          setGenerating(false);
          if (data.length > startCount) toast.success("New draft ready for review!");
        }
      }, 7000);
    } catch (err) {
      setGenerating(false);
      toast.error(err.response?.data?.detail || "Couldn't start generation");
    }
  };

  const approve = async (id) => {
    setBusy(true);
    try {
      await api.post(`/admin/topics/${id}/approve`);
      toast.success("Published live!");
      setDrafts((d) => d.filter((x) => x.topic_id !== id));
      onPublished?.();
    } catch {
      toast.error("Failed to publish");
    } finally { setBusy(false); }
  };

  const reject = async (id) => {
    setBusy(true);
    try {
      await api.post(`/admin/topics/${id}/reject`);
      toast.success("Draft rejected");
      setDrafts((d) => d.filter((x) => x.topic_id !== id));
    } catch {
      toast.error("Failed to reject");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-2xl shadow-card p-6 mb-6" data-testid="bot-drafts-panel">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h2 className="font-heading text-xl text-dark flex items-center gap-2">
          <Bot className="w-5 h-5 text-view-a" /> DEBATE BOT
          {drafts.length > 0 && <span className="text-xs font-body font-bold text-white bg-view-a rounded-full px-2 py-0.5">{drafts.length}</span>}
        </h2>
        <div className="flex gap-2">
          <button onClick={fetchDrafts} className="p-2 text-neutral hover:text-dark rounded-lg hover:bg-gray-100 transition-colors" title="Refresh" data-testid="refresh-drafts-btn">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={generateNow} disabled={generating} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-white bg-view-a rounded-xl hover:bg-view-a-dark transition-colors disabled:opacity-50" data-testid="generate-now-btn">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {generating ? "Generating…" : "Generate now"}
          </button>
        </div>
      </div>
      <p className="text-xs text-neutral flex items-center gap-1.5 mb-4">
        <Clock className="w-3.5 h-3.5" /> Auto-creates a draft from trending topics every 6 hours. Review and approve below to go live.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-neutral py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading drafts…</div>
      ) : drafts.length === 0 ? (
        <p className="text-sm text-neutral text-center py-6" data-testid="no-drafts">No pending drafts. Tap "Generate now" or wait for the next scheduled run.</p>
      ) : (
        <div className="space-y-3">
          {drafts.map((d) => <DraftCard key={d.topic_id} draft={d} onApprove={approve} onReject={reject} busy={busy} />)}
        </div>
      )}
    </div>
  );
}
